import React, { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchProperties } from "../../redux/propertiesSlice";
import { RootState, AppDispatch } from "../../redux/store";
import { Property } from "../../types/property";
import { useRecentlyViewed } from "../../hooks/useRecentlyViewed";
import { monthlyRentPcm } from "../../util/propertyParsing";
import SafeImage from "../SafeImage/SafeImage";

function getMainImage(property: Property): string | undefined {
  if (property.photos?.length) {
    return property.photos[0].media_url;
  }
  if (!property.media?.length) return undefined;
  const main =
    property.media.find((m) => m.caption?.includes("Main")) ||
    property.media.find((m) => m.media_type === 1 && m.sort_order === 1) ||
    property.media[0];
  return main?.media_url;
}

const RecentlyViewedCarousel: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { items: properties, status } = useSelector(
    (state: RootState) => state.properties
  );
  const { recentIds } = useRecentlyViewed();
  const navigate = useNavigate();

  useEffect(() => {
    if (status === "idle") {
      dispatch(fetchProperties());
    }
  }, [dispatch, status]);

  const recentProperties = useMemo(() => {
    return recentIds
      .map((id) => properties.find((p) => p.id === id))
      .filter((p): p is Property => Boolean(p));
  }, [recentIds, properties]);

  if (recentProperties.length === 0) return null;

  return (
    <section className="py-20 relative bg-bg-porcelain overflow-hidden">
      <div className="max-w-[1440px] mx-auto px-5">
        <div className="mb-12 pl-8 border-l-3 border-l-solid border-l-brand-yellow">
          <span className="text-xs tracking-widest uppercase text-text-slate block mb-2">
            Your history
          </span>
          <h2 className="page-section-heading">
            Recently Viewed
          </h2>
        </div>
        <div className="flex gap-6 pb-6 overflow-x-auto scroll-smooth [&::-webkit-scrollbar]:h-0.5 [&::-webkit-scrollbar-thumb]:bg-brand-yellow items-stretch">
          {recentProperties.map((property) => {
            const imageUrl = getMainImage(property);
            const pcm = monthlyRentPcm(property.price, property.rent_frequency);
            return (
              <div
                key={property.id}
                className="min-w-[280px] cursor-pointer relative group"
                onClick={() =>
                  navigate(`/services/rent/properties/${property.id}`)
                }
              >
                <div className="h-[220px] w-full relative overflow-hidden clip-path-[polygon(0_0,100%_0,100%_90%,0%_100%)]">
                  {imageUrl ? (
                    <SafeImage
                      src={imageUrl}
                      alt={property.display_address}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  ) : (
                    <div className="w-full h-full bg-gray-200 flex items-center justify-center text-text-slate text-sm">
                      No image
                    </div>
                  )}
                </div>
                <div className="pt-4">
                  <h3 className="font-teko text-xl text-text-obsidian truncate">
                    {property.display_address}
                  </h3>
                  <p className="font-manrope text-xs uppercase tracking-wider text-text-slate mt-1">
                    {property.bedrooms ?? "—"} bed • {property.property_type}
                  </p>
                  <p className="font-teko text-lg text-brand-yellow mt-2">
                    £{pcm.toLocaleString()} PCM
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default RecentlyViewedCarousel;
